import moment from "moment";
import { convertFromUTC } from "utils/datetime";

export const MAX_SUMMARY_DAYS = 31;
export const WORK_SHIFT_TYPE = "Trabajo";
export const ALERT_LATE = "TIME_REGISTRATION_START_TOO_LATE";
export const ALERT_EARLY = "TIME_REGISTRATION_END_TOO_EARLY";
export const ALERT_NOT_DONE = "TIME_REGISTRATION_NOT_DONE";

const SUMMARY_ALERTS = new Set([ALERT_LATE, ALERT_EARLY, ALERT_NOT_DONE]);

const PAUSE_TYPE = /pausa|descanso/i;

/**
 * Las alertas a veces nombran un día pegado al periodo (el turno de después)
 * y el fichaje de ese día no entra en la consulta. Hay que pedirlo aparte.
 * Solo se amplía un par de días: un aviso lejano no dispara otra lectura larga.
 */
export function spilledAlertRange(alerts, startDate, endDate, maxExtraDays = 2) {
	const start = moment(startDate, "YYYY-MM-DD", true);
	const end = moment(endDate, "YYYY-MM-DD", true);
	if (!start.isValid() || !end.isValid()) return null;
	let min = null;
	let max = null;
	(alerts || []).forEach((alert) => {
		const day = moment(alert?.day).format("YYYY-MM-DD");
		const parsed = moment(day, "YYYY-MM-DD", true);
		if (!parsed.isValid()) return;
		if (!parsed.isBefore(start, "day") && !parsed.isAfter(end, "day")) return;
		const distance = Math.min(
			Math.abs(parsed.diff(start, "days")),
			Math.abs(parsed.diff(end, "days"))
		);
		if (distance > maxExtraDays) return;
		if (!min || day < min) min = day;
		if (!max || day > max) max = day;
	});
	if (!min) return null;
	return { startDate: min, endDate: max };
}

export function widenSummaryRange(filters, spilled) {
	if (!spilled) return filters;
	const startDate =
		filters.startDate < spilled.startDate ? filters.startDate : spilled.startDate;
	const endDate = filters.endDate > spilled.endDate ? filters.endDate : spilled.endDate;
	return { ...filters, startDate, endDate };
}

export function isAllowedSummaryPeriod(start, end) {
	if (!start || !end) return false;
	const startDay = moment(start).startOf("day");
	const endDay = moment(end).startOf("day");
	if (!startDay.isValid() || !endDay.isValid() || endDay.isBefore(startDay)) {
		return false;
	}
	return endDay.diff(startDay, "days") + 1 <= MAX_SUMMARY_DAYS;
}

/**
 * Si el fin no es posterior al inicio, pasa al día siguiente.
 * Si la duración supera 24 h, resta un día. Misma regla que Mis fichajes.
 */
export function adjustRegistrationEnd(start, end) {
	let startLocal = moment(start);
	let endLocal = moment(end);
	if (!startLocal.isValid() || !endLocal.isValid()) return endLocal;
	if (!endLocal.isAfter(startLocal)) {
		endLocal = endLocal.clone().add(1, "day");
	}
	if (endLocal.diff(startLocal, "hours", true) > 24) {
		endLocal = endLocal.clone().subtract(1, "day");
	}
	return endLocal;
}

export function formatSummaryMinutes(minutes) {
	if (minutes === null || minutes === undefined) return "—";
	const safe = Math.max(0, Math.round(minutes));
	const hours = Math.floor(safe / 60);
	const mins = safe % 60;
	return `${hours}h ${String(mins).padStart(2, "0")}m`;
}

export function filterSummaryEmployees(employees, kpi) {
	if (kpi === "alerts") return employees.filter((employee) => employee.reviewCount > 0);
	if (kpi === "open") return employees.filter((employee) => employee.open > 0);
	if (kpi === "missing") return employees.filter((employee) => employee.missing > 0);
	if (kpi === "timing") {
		return employees.filter((employee) => employee.late > 0 || employee.early > 0);
	}
	return employees;
}

export function employeesWithAlerts(employees) {
	return (employees || []).filter((employee) => employee.reviewCount > 0);
}

export function periodEmployeeMark(employee) {
	return {
		alerts: employee.reviewCount > 0,
		missing: employee.missing > 0,
	};
}

export function periodTotalsFromMarks(marks) {
	const rows = marks instanceof Map ? [...marks.values()] : marks || [];
	return {
		staff: rows.length,
		withAlerts: rows.filter((row) => row.alerts).length,
		missingEmployees: rows.filter((row) => row.missing).length,
	};
}

export function summarizeEmployees(employees) {
	const rows = employees || [];
	return {
		staff: rows.length,
		withAlerts: rows.filter((employee) => employee.reviewCount > 0).length,
		openEmployees: rows.filter((employee) => employee.open > 0).length,
		missingEmployees: rows.filter((employee) => employee.missing > 0).length,
		missingShifts: rows.reduce((sum, employee) => sum + employee.missing, 0),
		lateRows: rows.reduce((sum, employee) => sum + employee.late, 0),
		earlyRows: rows.reduce((sum, employee) => sum + employee.early, 0),
		reviewRows: rows.reduce((sum, employee) => sum + employee.reviewCount, 0),
	};
}

export function sortSummaryEmployees(employees, sort) {
	const field = sort?.field || "name";
	const direction = sort?.direction === "desc" ? -1 : 1;
	return [...employees].sort((a, b) => {
		let cmp = 0;
		if (field === "clocked") {
			const ratio = (employee) => (employee.planned ? employee.clocked / employee.planned : 0);
			cmp = ratio(a) - ratio(b) || a.clocked - b.clocked;
		} else if (field === "alerts") {
			cmp = a.reviewCount - b.reviewCount;
		} else {
			cmp = a.name.localeCompare(b.name, "es", { sensitivity: "base" });
		}
		if (cmp === 0) {
			cmp = a.name.localeCompare(b.name, "es", { sensitivity: "base" });
		}
		return cmp * direction;
	});
}

/**
 * Tras editar o aprobar, el empleado se queda en el sitio aunque ya no tenga alerta.
 * Quien entra nuevo en la lista se añade al final.
 */
export function preserveSummaryStaff(previousStaff, builtEmployees, stickyIds) {
	const alertList = employeesWithAlerts(builtEmployees);
	const sticky = stickyIds instanceof Set ? stickyIds : new Set(stickyIds || []);
	if (sticky.size === 0) return alertList;
	const byId = new Map(
		(builtEmployees || []).map((employee) => [String(employee.id), employee])
	);
	const next = [];
	const used = new Set();
	(previousStaff || []).forEach((prev) => {
		const id = String(prev.id);
		const updated = byId.get(id);
		if (!updated) return;
		if ((updated.reviewCount || 0) > 0 || sticky.has(id)) {
			next.push(updated);
			used.add(id);
		}
	});
	alertList.forEach((employee) => {
		const id = String(employee.id);
		if (!used.has(id)) next.push(employee);
	});
	return next;
}

/**
 * Mantiene el orden que veía el usuario. `orderedIds` se resuelve sobre la lista
 * completa; `extrasFrom` solo añade quien sigue pasando el filtro y no estaba.
 */
export function keepSummaryOrder(employees, orderedIds, extrasFrom) {
	const source = employees || [];
	if (!orderedIds?.length) return extrasFrom || source;
	const byId = new Map(source.map((employee) => [String(employee.id), employee]));
	const next = [];
	const seen = new Set();
	orderedIds.forEach((id) => {
		const key = String(id);
		const employee = byId.get(key);
		if (!employee || seen.has(key)) return;
		next.push(employee);
		seen.add(key);
	});
	(extrasFrom || []).forEach((employee) => {
		const key = String(employee.id);
		if (!seen.has(key)) next.push(employee);
	});
	return next;
}

export function matchesSummaryUbication(entity, ubicationId) {
	const wanted = Number(ubicationId);
	if (!wanted) return true;
	const ids = [];
	if (entity?.ubication?.id != null) ids.push(Number(entity.ubication.id));
	if (entity?.ubication_id != null) ids.push(Number(entity.ubication_id));
	(entity?.ubications || []).forEach((item) => {
		const id = item?.id ?? item;
		if (id != null) ids.push(Number(id));
	});
	return ids.includes(wanted);
}

export function filterAssignmentsByUbication(groups, ubicationId) {
	if (!Number(ubicationId)) return groups || [];
	return (groups || []).map((group) => ({
		...group,
		plans: (group.plans || []).map((plan) => ({
			...plan,
			assignments: (plan.assignments || []).filter((assignment) =>
				matchesSummaryUbication(assignment, ubicationId)
			),
		})),
	}));
}

export function alertEmployeeId(alert) {
	return String(alert?.employee_id || alert?.employee?.id || "");
}

export function employeeIdsMissingFromRegistrations(alerts, registrations) {
	const present = new Set();
	(registrations || []).forEach((item) => {
		const id = item?.employee?.id || item?.employee_id;
		if (id) present.add(String(id));
	});
	const missing = [];
	const seen = new Set();
	(alerts || []).forEach((alert) => {
		if (!SUMMARY_ALERTS.has(alert?.type)) return;
		const id = alertEmployeeId(alert);
		if (!id || present.has(id) || seen.has(id)) return;
		seen.add(id);
		missing.push(id);
	});
	return missing;
}

/**
 * La ubicación no viaja en la alerta. Los fichajes se filtran por la suya.
 * Un «sin fichar» se queda si el empleado ya tiene un fichaje en esa ubicación
 * o si alguno de sus turnos cae ahí.
 */
export function scopeSummaryToUbication(
	registrations,
	alerts,
	ubicationId,
	assignments = []
) {
	if (!Number(ubicationId)) {
		return { registrations: registrations || [], alerts: alerts || [] };
	}
	const keptRegistrations = (registrations || []).filter((item) =>
		matchesSummaryUbication(item, ubicationId)
	);
	const keptIds = new Set(keptRegistrations.map((item) => String(item.id)));
	const keptEmployees = new Set(
		keptRegistrations.map((item) => String(item.employee?.id || item.employee_id || ""))
	);
	const shiftEmployees = new Set();
	filterAssignmentsByUbication(assignments, ubicationId).forEach((group) => {
		const id = String(group.employee?.id || group.employee_id || "");
		const hasShift = (group.plans || []).some((plan) => (plan.assignments || []).length > 0);
		if (id && hasShift) shiftEmployees.add(id);
	});
	const keptAlerts = (alerts || []).filter((alert) => {
		const registrationId = alert?.time_registration_id;
		if (registrationId != null && registrationId !== "") {
			return keptIds.has(String(registrationId));
		}
		const employeeId = alertEmployeeId(alert);
		return keptEmployees.has(employeeId) || shiftEmployees.has(employeeId);
	});
	return { registrations: keptRegistrations, alerts: keptAlerts };
}

export function buildEmployeeSummary({
	registrations = [],
	alerts = [],
	assignments = [],
	employees: directory = [],
	plannedHours = [],
	now = moment(),
} = {}) {
	const current = moment(now);
	const shifts = flattenWorkShifts(assignments);
	const alertsByRegistration = indexAlerts(alerts);
	const plannedByEmployee = sumPlannedHours(plannedHours);
	const knownEmployees = indexDirectory(directory);
	const buckets = new Map();

	const ensure = (employee, employeeId) => {
		const id = String(employeeId || "");
		if (!id) return null;
		if (!buckets.has(id)) {
			const person = employee?.name ? employee : knownEmployees.get(id) || employee;
			buckets.set(id, {
				id,
				employee: person || { id },
				name: employeeName(person),
				shifts: [],
				registrations: [],
				notDone: [],
			});
		} else if (employee?.name && !buckets.get(id).employee?.name) {
			const bucket = buckets.get(id);
			bucket.employee = employee;
			bucket.name = employeeName(employee);
		}
		return buckets.get(id);
	};

	registrations.forEach((registration) => {
		const employeeId = registration.employee?.id || registration.employee_id;
		if (!employeeId || !registration.start) return;
		ensure(registration.employee, employeeId).registrations.push(registration);
	});

	(alerts || []).forEach((alert) => {
		if (alert?.type !== ALERT_NOT_DONE) return;
		const employeeId = alertEmployeeId(alert);
		const bucket = ensure(personForAlert(alert, knownEmployees), employeeId);
		if (!bucket) return;
		bucket.notDone.push(alert);
	});

	shifts.forEach((shift) => {
		const bucket = buckets.get(String(shift.employeeId));
		if (bucket) bucket.shifts.push(shift);
	});

	const rows = [];
	buckets.forEach((bucket) => {
		const built = buildEmployee(bucket, alertsByRegistration, current);
		built.plannedHours = plannedByEmployee[bucket.id] ?? null;
		rows.push(built);
	});

	const totals = summarizeEmployees(rows);

	return {
		employees: sortSummaryEmployees(rows, { field: "name", direction: "asc" }),
		totals,
	};
}

function buildEmployee(bucket, alertsByRegistration, now) {
	const shiftByRegistration = matchRegistrations(bucket.registrations, bucket.shifts, now);
	const coveredShiftIds = new Set();

	bucket.registrations.forEach((registration) => {
		if (isPauseRegistration(registration)) return;
		const start = moment(registration.start);
		const end = registration.end
			? adjustRegistrationEnd(registration.start, registration.end)
			: now;
		bucket.shifts.forEach((shift) => {
			if (rangesOverlap(start, end, shift.start, shift.end)) {
				coveredShiftIds.add(shift.id);
			}
		});
	});

	const detail = [];

	bucket.registrations.forEach((registration) => {
		const shift = shiftByRegistration.get(registration.id) || null;
		const missingEnd = !registration.end || !moment(registration.end).isValid();
		const day = moment(registration.start).format("YYYY-MM-DD");
		const staleOpen = missingEnd && day < now.format("YYYY-MM-DD");
		const flags = alertsByRegistration.get(String(registration.id)) || {
			late: false,
			early: false,
			plannedStart: "",
			plannedEnd: "",
		};
		const workedMinutes = missingEnd
			? null
			: workedMinutesBetween(registration.start, registration.end);
		const inReview = staleOpen || flags.late || flags.early;
		detail.push({
			key: `reg-${registration.id}`,
			employeeId: bucket.id,
			day,
			status: staleOpen ? "open" : missingEnd ? "progress" : "closed",
			late: flags.late,
			early: flags.early,
			inReview,
			canApprove: !missingEnd,
			canCloseAtPlanned: missingEnd && Boolean(shift?.endHm || flags.plannedEnd),
			canRegisterAtPlanned: false,
			canDelete: true,
			canEdit: true,
			registration,
			ubication: registration.ubication || shift?.ubication || null,
			typeKind: staleOpen ? "open" : typeKind(registration),
			typeName: registration.type?.name || "",
			workedMinutes,
			clockStart: moment(registration.start),
			clockEnd: missingEnd ? null : adjustRegistrationEnd(registration.start, registration.end),
			plannedStartHm: shift?.startHm || flags.plannedStart || "",
			plannedEndHm: shift?.endHm || flags.plannedEnd || "",
			approved: Boolean(registration.approved),
			sortAt: moment(registration.start).valueOf(),
		});
	});

	const seenMissingDays = new Set();
	(bucket.notDone || []).forEach((alert) => {
		const day = moment(alert.day).format("YYYY-MM-DD");
		if (!moment(day, "YYYY-MM-DD", true).isValid()) return;
		if (seenMissingDays.has(day) || dayHasWorkRegistration(bucket.registrations, day)) return;
		seenMissingDays.add(day);
		const shift = bucket.shifts.find((candidate) => candidate.day === day) || null;
		const plannedStartHm = shift?.startHm || toLocalHm(alert.plan_time || alert.planTime);
		const plannedEndHm = shift?.endHm || "";
		detail.push({
			key: `missing-${bucket.id}-${day}`,
			employeeId: bucket.id,
			day,
			status: "missing",
			late: false,
			early: false,
			inReview: true,
			canApprove: false,
			canCloseAtPlanned: false,
			canRegisterAtPlanned: Boolean(plannedStartHm && plannedEndHm),
			canDelete: false,
			canEdit: true,
			registration: null,
			ubication: shift?.ubication || null,
			typeKind: "missing",
			typeName: "",
			workedMinutes: 0,
			clockStart: null,
			clockEnd: null,
			plannedStartHm,
			plannedEndHm,
			approved: false,
			sortAt: moment(`${day} ${plannedStartHm || "00:00"}`, "YYYY-MM-DD HH:mm").valueOf(),
		});
	});

	detail.sort((a, b) => a.sortAt - b.sortAt || a.day.localeCompare(b.day));

	const open = detail.filter((row) => row.status === "open").length;
	const missing = detail.filter((row) => row.status === "missing").length;
	const late = detail.filter((row) => row.late).length;
	const early = detail.filter((row) => row.early).length;
	const reviewCount = detail.filter((row) => row.inReview).length;
	const workedMinutes = detail.reduce(
		(sum, row) => sum + (row.status === "closed" ? row.workedMinutes || 0 : 0),
		0
	);
	const pendingCount = detail.filter((row) => row.canApprove && !row.approved).length;
	const approvedCount = detail.filter((row) => row.canApprove && row.approved).length;
	const attendance = attendanceCounts(bucket, detail, coveredShiftIds);

	return {
		id: bucket.id,
		employee: bucket.employee,
		name: bucket.name,
		clocked: attendance.clocked,
		planned: attendance.planned,
		open,
		missing,
		late,
		early,
		reviewCount,
		workedMinutes,
		plannedHours: null,
		approvedCount,
		pendingCount,
		rows: detail,
	};
}

function matchRegistrations(registrations, shifts, now) {
	const used = new Set();
	const matched = new Map();
	const ordered = [...registrations].sort(
		(a, b) => moment(a.start).valueOf() - moment(b.start).valueOf()
	);

	ordered.forEach((registration) => {
		if (isPauseRegistration(registration)) return;
		const shift = findContainingShift(registration, shifts, used);
		if (!shift) return;
		used.add(shift.id);
		matched.set(registration.id, shift);
	});

	ordered.forEach((registration) => {
		if (matched.has(registration.id) || isPauseRegistration(registration)) return;
		const day = moment(registration.start).format("YYYY-MM-DD");
		const start = moment(registration.start);
		const end = registration.end
			? adjustRegistrationEnd(registration.start, registration.end)
			: now;
		const shift = shifts.find(
			(candidate) =>
				!used.has(candidate.id) &&
				candidate.day === day &&
				rangesOverlap(start, end, candidate.start, candidate.end)
		);
		if (!shift) return;
		used.add(shift.id);
		matched.set(registration.id, shift);
	});

	ordered.forEach((registration) => {
		if (matched.has(registration.id)) return;
		const shift = findContainingShift(registration, shifts, new Set());
		if (shift) matched.set(registration.id, shift);
	});

	return matched;
}

function findContainingShift(registration, shifts, used) {
	const start = moment(registration.start);
	return shifts.find(
		(shift) =>
			!used.has(shift.id) &&
			!start.isBefore(shift.start) &&
			start.isBefore(shift.end)
	);
}

function flattenWorkShifts(groups) {
	const shifts = [];
	(groups || []).forEach((group) => {
		const employee = group.employee || {};
		const employeeId = employee.id || group.employee_id;
		if (!employeeId) return;
		(group.plans || []).forEach((plan) => {
			const day = moment(plan.day).format("YYYY-MM-DD");
			if (!moment(day, "YYYY-MM-DD", true).isValid()) return;
			(plan.assignments || []).forEach((assignment, index) => {
				if (assignment.shift?.type !== WORK_SHIFT_TYPE) return;
				const intervals = shiftIntervals(assignment);
				const bounds = spanIntervals(day, intervals);
				if (!bounds) return;
				shifts.push({
					id: String(assignment.id || `${employeeId}_${day}_${index}`),
					employee,
					employeeId: String(employeeId),
					day,
					start: bounds.start,
					end: bounds.end,
					startHm: bounds.start.format("HH:mm"),
					endHm: bounds.end.format("HH:mm"),
					ubication:
						assignment.ubication || assignment.ubications?.[0] || null,
				});
			});
		});
	});
	return shifts;
}

function shiftIntervals(assignment) {
	const own = assignment.time_intervals;
	const fromShift = assignment.shift?.time_intervals;
	const source = own?.length ? own : fromShift || [];
	return source
		.map((interval) => ({
			start: toLocalHm(interval.start),
			end: toLocalHm(interval.end),
		}))
		.filter((interval) => interval.start && interval.end);
}

function spanIntervals(day, intervals) {
	let start = null;
	let end = null;
	intervals.forEach((interval) => {
		const bounds = summaryShiftBounds(day, interval.start, interval.end);
		if (!bounds) return;
		if (!start || bounds.start.isBefore(start)) start = bounds.start;
		if (!end || bounds.end.isAfter(end)) end = bounds.end;
	});
	if (!start || !end) return null;
	return { start, end };
}

export function summaryShiftBounds(day, startHm, endHm) {
	const start = moment(`${day} ${startHm}`, "YYYY-MM-DD HH:mm", true);
	let end = moment(`${day} ${endHm}`, "YYYY-MM-DD HH:mm", true);
	if (!start.isValid() || !end.isValid()) return null;
	if (!end.isAfter(start)) end = end.clone().add(1, "day");
	return { start, end };
}

function toLocalHm(value) {
	if (value == null || value === "") return "";
	const fromUtc = convertFromUTC(value);
	if (fromUtc) return fromUtc;
	if (typeof value === "string" && /^\d{2}:\d{2}/.test(value.trim())) {
		return value.trim().slice(0, 5);
	}
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format("HH:mm") : "";
}

function indexAlerts(alerts) {
	const map = new Map();
	(alerts || []).forEach((alert) => {
		const registrationId = alert?.time_registration_id ?? alert?.timeRegistrationId;
		if (registrationId == null || registrationId === "") return;
		const key = String(registrationId);
		const current = map.get(key) || {
			late: false,
			early: false,
			plannedStart: "",
			plannedEnd: "",
		};
		const plan = toLocalHm(alert.plan_time || alert.planTime);
		if (alert.type === ALERT_LATE) {
			current.late = true;
			if (plan) current.plannedStart = plan;
		}
		if (alert.type === ALERT_EARLY) {
			current.early = true;
			if (plan) current.plannedEnd = plan;
		}
		map.set(key, current);
	});
	return map;
}

function indexDirectory(employees) {
	const map = new Map();
	(employees || []).forEach((person) => {
		if (person?.id == null) return;
		map.set(String(person.id), person);
	});
	return map;
}

function personForAlert(alert, directory) {
	const id = alertEmployeeId(alert);
	if (directory.has(id)) return directory.get(id);
	if (alert?.employee?.name) return alert.employee;
	return { id, name: alert?.employee_name || alert?.employeeName || "", surname: "" };
}

function dayHasWorkRegistration(registrations, day) {
	return (registrations || []).some((registration) => {
		if (!registration?.start || isPauseRegistration(registration)) return false;
		return moment(registration.start).format("YYYY-MM-DD") === day;
	});
}

function attendanceCounts(bucket, detail, coveredShiftIds) {
	if (bucket.shifts.length) {
		return { clocked: coveredShiftIds.size, planned: bucket.shifts.length };
	}
	const clockedDays = new Set();
	const missingDays = new Set();
	detail.forEach((row) => {
		if (row.status === "missing") missingDays.add(row.day);
		else if (row.typeKind !== "pause") clockedDays.add(row.day);
	});
	return { clocked: clockedDays.size, planned: clockedDays.size + missingDays.size };
}

function sumPlannedHours(rows) {
	const map = {};
	(rows || []).forEach((row) => {
		const id = String(row.employee?.id || row.employee_id || "");
		if (!id) return;
		map[id] = (map[id] || 0) + (Number(row.value) || 0);
	});
	return map;
}

function rangesOverlap(aStart, aEnd, bStart, bEnd) {
	return aStart.isBefore(bEnd) && bStart.isBefore(aEnd);
}

function workedMinutesBetween(start, end) {
	const adjusted = adjustRegistrationEnd(start, end);
	const minutes = adjusted.diff(moment(start), "minutes");
	return minutes > 0 ? minutes : 0;
}

function isPauseRegistration(registration) {
	return PAUSE_TYPE.test(registration.type?.name || "");
}

function typeKind(registration) {
	if (isPauseRegistration(registration)) return "pause";
	if ((registration.type?.name || "") === WORK_SHIFT_TYPE) return "work";
	return "other";
}

function employeeName(employee) {
	if (!employee) return "";
	return `${employee.name || ""} ${employee.surname || ""}`.trim();
}
